import type { AxiosError } from 'axios'
import axios from 'axios'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { GLOBAL_ERROR } from 'core/constants/translations/error'
import { useTranslation } from 'react-i18next'
import { useFormatNumber } from 'core/hooks/useFormatNumber'
import type { ForecastBelowRealizedAccount, ForecastBelowRealizedErrorResponse } from './forecastBelowRealizedError'
import { FORECAST_BELOW_REALIZED_ERROR_CODE } from './forecastBelowRealizedError'
import type { StoreForecastErrorResponse } from 'core/api/trajectory-photo/storeForecastError'

interface StoreForecastInstoreAccountValues {
  digital_gmv: number | null
  physical_gmv: number | null
  decapro_gmv: number | null
  eco_circular_repair_and_maintenance_gmv: number | null
  eco_circular_saved_products_gmv: number | null
  eco_circular_buy_back_gmv: number | null
  eco_circular_rental_gmv: number | null
  margin_rate: number | null
  quantity: number | null
}

interface StoreForecastOutstoreAccountValues {
  gmv: number | null
  margin_rate: number | null
  quantity: number | null
}

interface StoreForecastAccountValues {
  instore: StoreForecastInstoreAccountValues
  outstore: StoreForecastOutstoreAccountValues
}

export interface SaveStoreYearlyForecastVariables {
  storeId: number
  referenceStoreId: number | null
  year: number
  accountValues: StoreForecastAccountValues
}

const isMarginRateAccount = (account: ForecastBelowRealizedAccount) =>
  account === 'instore_margin_rate' || account === 'outstore_margin_rate'

const ACCOUNT_TRANSLATION_KEY_BY_ACCOUNT: Record<ForecastBelowRealizedAccount, string> = {
  physical_gmv: 'general.trajectory.store.edit.instore_gmv_segment.sales_channels.physical',
  digital_gmv: 'general.trajectory.store.edit.instore_gmv_segment.sales_channels.digital',
  decapro_gmv: 'general.trajectory.store.edit.instore_gmv_segment.sales_channels.decapro',
  eco_circular_gmv: 'general.trajectory.store.recap.economy.circular',
  eco_non_circular_gmv: 'general.trajectory.store.recap.economy.non-circular',
  eco_circular_repair_and_maintenance_gmv: 'general.trajectory.store.recap.circular.repair',
  eco_circular_saved_products_gmv: 'general.trajectory.store.recap.circular.second-life',
  eco_circular_buy_back_gmv: 'general.trajectory.store.recap.circular.buy-back',
  eco_circular_rental_gmv: 'general.trajectory.store.recap.circular.rental',
  outstore_gmv: 'general.trajectory.store.recap.outstore',
  instore_margin_rate: 'account.INSTORE_MARGIN_RATE',
  outstore_margin_rate: 'account.OUTSTORE_MARGIN_RATE',
  instore_quantity: 'account.INSTORE_QUANTITY',
  outstore_quantity: 'account.OUTSTORE_QUANTITY'
}

const saveStoreYearlyForecast = async ({
  storeId,
  referenceStoreId,
  year,
  accountValues
}: SaveStoreYearlyForecastVariables): Promise<void> => {
  const params = referenceStoreId ? `?referenceStoreId=${referenceStoreId}` : ''
  const url = `/v1/stores/${storeId}/forecast/year/${year}${params}`
  const payload: StoreForecastAccountValues = {
    instore: {
      digital_gmv: accountValues.instore.digital_gmv,
      physical_gmv: accountValues.instore.physical_gmv,
      decapro_gmv: accountValues.instore.decapro_gmv,
      eco_circular_repair_and_maintenance_gmv: accountValues.instore.eco_circular_repair_and_maintenance_gmv,
      eco_circular_saved_products_gmv: accountValues.instore.eco_circular_saved_products_gmv,
      eco_circular_buy_back_gmv: accountValues.instore.eco_circular_buy_back_gmv,
      eco_circular_rental_gmv: accountValues.instore.eco_circular_rental_gmv,
      margin_rate: accountValues.instore.margin_rate,
      quantity: accountValues.instore.quantity
    },
    outstore: {
      gmv: accountValues.outstore.gmv,
      margin_rate: accountValues.outstore.margin_rate,
      quantity: accountValues.outstore.quantity
    }
  }
  return axios.post(url, payload)
}

export const useApiDoStoreYearlyForecast = () => {
  const { t } = useTranslation()
  const formatNumber = useFormatNumber()
  const queryClient = useQueryClient()

  return useMutation<
    void,
    AxiosError<ForecastBelowRealizedErrorResponse | StoreForecastErrorResponse>,
    SaveStoreYearlyForecastVariables
  >({
    mutationFn: saveStoreYearlyForecast,
    onSuccess: () => {
      toast.info(t('general.trajectory.store.save.success'))
      void queryClient.invalidateQueries({ queryKey: ['get-store-forecast'] })
    },
    onError: error => {
      const data = error.response?.data
      if (data?.error_code === FORECAST_BELOW_REALIZED_ERROR_CODE) {
        const yearToDateRealized = isMarginRateAccount(data.account)
          ? formatNumber(data.year_to_date_realized, '--', {
              style: 'percent',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })
          : formatNumber(data.year_to_date_realized)
        toast.error(
          t('general.trajectory.store.error.below_year_to_date_realized', {
            account: t(ACCOUNT_TRANSLATION_KEY_BY_ACCOUNT[data.account]),
            yearToDateRealized
          })
        )
      } else {
        toast.error(t(GLOBAL_ERROR))
      }
    }
  })
}
